/**
 * A GitHub change request head as a run input. Run inputs are strings, so the ref travels
 * as one string: {@link formatChangeRequestRef} writes it, {@link parseChangeRequestRef}
 * reads it back.
 *
 * @since 1.0.0
 */
import { Option, Schema } from "effect";

const SHA = /^[0-9a-f]{40}$/;
const BRANCH = /^[^\s:]+$/; // git refnames hold no whitespace and no `:`

/**
 * A GitHub change request head as a run input. `number`: absent for a push.
 * - A pull request carries `baseBranch` (its base ref: `main`, `staging`, `project/x`) and may
 *   carry `baseSha`.
 * - A push carries `baseSha` (the pre-push `before`) and `baseBranch` (the pushed branch). A push
 *   that creates the branch (`before` all zeros) carries neither.
 * Consumers diff against `baseSha` when set, else the head of `baseBranch`.
 *
 * @since 1.0.0
 */
export const ChangeRequestRef = Schema.Struct({
  /** `owner/name`. */
  repo: Schema.String.pipe(Schema.pattern(/^[A-Za-z0-9-]+\/[A-Za-z0-9._-]+$/)),
  number: Schema.optional(Schema.Int.pipe(Schema.positive())),
  headSha: Schema.String.pipe(Schema.pattern(SHA)),
  headBranch: Schema.String.pipe(Schema.pattern(BRANCH)),
  baseBranch: Schema.optional(Schema.String.pipe(Schema.pattern(BRANCH))),
  baseSha: Schema.optional(Schema.String.pipe(Schema.pattern(SHA))),
}).annotations({ identifier: "ChangeRequestRef" });
export type ChangeRequestRef = typeof ChangeRequestRef.Type;

/**
 * `<repo>[#<number>]@<headSha>:<headBranch>[:baseBranch=<branch>][:baseSha=<sha>]`, each
 * bracket present only with its field. e.g. `acme/app#12@<sha>:feat/x:baseBranch=main:baseSha=<sha>`
 * (a pull request), `acme/app@<after>:main:baseBranch=main:baseSha=<before>` (a push).
 *
 * @since 1.0.0
 */
export const formatChangeRequestRef = (ref: ChangeRequestRef): string =>
  [
    ref.repo,
    ref.number === undefined ? "" : `#${ref.number}`,
    `@${ref.headSha}:${ref.headBranch}`,
    ref.baseBranch === undefined ? "" : `:baseBranch=${ref.baseBranch}`,
    ref.baseSha === undefined ? "" : `:baseSha=${ref.baseSha}`,
  ].join("");

// No field holds `:`, so the split is unambiguous even when a branch holds `/`, `@`, `#` or `=`.
const REF_TEXT =
  /^([^#@:\s]+)(?:#([1-9]\d*))?@([0-9a-f]{40}):([^:]+)(?::baseBranch=([^:]+))?(?::baseSha=([0-9a-f]{40}))?$/;

const decodeRef = Schema.decodeUnknownOption(ChangeRequestRef);

/**
 * The ref a run input value names, or none. Inverse of the formatter.
 *
 * @since 1.0.0
 */
export const parseChangeRequestRef = (text: string): Option.Option<ChangeRequestRef> =>
  Option.fromNullable(REF_TEXT.exec(text)).pipe(
    Option.flatMap(([, repo, number, headSha, headBranch, baseBranch, baseSha]) =>
      decodeRef({
        repo,
        ...(number === undefined ? {} : { number: Number(number) }),
        headSha,
        headBranch,
        ...(baseBranch === undefined ? {} : { baseBranch }),
        ...(baseSha === undefined ? {} : { baseSha }),
      })
    ),
  );
